import {
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  CircularProgress,
  Box,
  Divider,
  ListSubheader,
  ListItemText,
} from '@mui/material';
import {CSSProperties, useState, useEffect, useRef} from 'react';
import {
  VariableSizeList,
  FixedSizeList,
  ListChildComponentProps,
} from 'react-window';
import InfiniteLoader from 'react-window-infinite-loader';
import {t} from 'i18next';
import AurIcon, {IconNames, IconColors} from '../aur-icon/AurIcon';
import {Option} from '../aur-select/AurSelect';

import styles from './style.module.scss';
import AurSearchBox from '../aur-search-box/AurSearchBox';
import {useFieldFocusStyle} from '../../../hooks/utils/useFieldFocusStyle';

const ITEM_HEIGHT = 36;
const ITEMS_VISIBLE = 11;
const SEARCH_BOX_HEIGHT = 80;
const SCROLL_BAR_STYLE_CLASS = 'aur-item-container-scroll';
const DEFAULT_MUI_BORDER = 2; // default border (top + bottom) for dropdown menu provided by the MUI Library
const MUI_CANVAS_ADJUSTMENT = 44; // default bottom-padding applied by MUI Library
const CANVAS_UPPER_ADJUSTMENT = 8; // adjust the dropdown position to upper direction by addning extra padding/margin

interface AurSelectProps {
  label: string;
  optionList: Option[];
  field: {
    value: string | undefined;
    label: string | undefined;
    onChange: (selectedOption: Option) => void;
    onBlur?: () => void;
    name?: string | undefined;
  };
  fieldState: {
    error?: {
      message?: string;
    };
  };
  labelId?: string;
  onSelect?: (selectedOption: Option) => void;
  disabled?: boolean;
  helperText?: string;
  dataTestId?: string;
  style?: CSSProperties;
  onSearch?: (keyword: string) => Promise<Option[]>;
  searchable?: boolean;

  // Server-side processing
  itemsPerPage?: number;
  onPage?: (startIndex: number, stopIndex: number) => Promise<Option[]>;
  hasNextPage?: boolean;
  isLoading?: boolean; //Stops paging from happening if initial load is still running.
  isVariableHeight?: boolean;
  itemsVisible?: number;
  anchorBottonPosition?: boolean;
  customProjectClassName?: string;
}

const AurServerSingleSelect = (props: AurSelectProps) => {
  const {
    label,
    optionList,
    field,
    fieldState,
    labelId,
    onSelect,
    disabled = false,
    helperText = '',
    dataTestId,
    style,
    itemsPerPage = 100,
    onPage,
    hasNextPage = false,
    searchable,
    onSearch,
    isLoading,
    isVariableHeight = false,
    itemsVisible = ITEMS_VISIBLE,
    customProjectClassName,
  } = props;

  const [itemData, setItemData] = useState<Option[]>([]);
  const [searchResults, setSearchResults] = useState<Option[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const selectComponentRef = useRef<HTMLInputElement | null>(null);
  const popUpContainerRef = useRef<HTMLDivElement | null>(null);
  const [menuPosition, setMenuPosition] = useState(0);
  const [calculatedDropdownHeight, setCalculatedDropdownHeight] =
    useState<number>(0);
  const [selectedItem, setSelectedItem] = useState<Option>({
    key: field.value ?? '',
    value: field.value ?? '',
    label: field.label ?? '',
  });
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const {borderClass, focusHandler, mouseKeyDownHandler} = useFieldFocusStyle({
    tabFocusClass: styles.tabFocusedBorder,
    mouseFocusClass: styles.mouseFocusedBorder,
  });

  const genId = labelId || `demo-simple-select-label-${label}`;
  // Observe window resize
  useEffect(() => {
    // Run on mount and scroll/resize
    window.addEventListener('scroll', calculateDirection, true);
    window.addEventListener('resize', calculateDirection);

    return () => {
      window.removeEventListener('scroll', calculateDirection, true);
      window.removeEventListener('resize', calculateDirection);
    };
  }, []);

  const conditionalPositioningProp =
    menuPosition < 0
      ? {marginTop: `${menuPosition}px`}
      : {paddingTop: `${menuPosition}px`};

  useEffect(() => {
    if (field.value) {
      const match = optionList.find((item) => item.value === field.value);
      if (match) {
        setSelectedItem(match);
      } else {
        setSelectedItem({
          key: field.value,
          value: field.value,
          label: field.label ?? '',
        });
      }
    } else {
      setSelectedItem({key: '', value: '', label: ''});
    }
  }, [field.value, field.label]);

  useEffect(() => {
    setItemData(optionList);
  }, [optionList]);

  useEffect(() => {
    if (isSearching) loadSearchResults(searchTerm);
  }, [isSearching, searchTerm]);

  const onClickDelegate = (item: Option) => {
    field?.onChange(item);
    setSelectedItem(item);
    setIsDropdownOpen(!isDropdownOpen);
    onSelect && onSelect(item);
  };

  const onKeyUpDelegate = (event: React.KeyboardEvent, item: Option) => {
    if (event.key === 'Enter') {
      event.stopPropagation();
      onClickDelegate(item);
    }
  };

  const isItemLoaded = (index: number) => {
    return !hasNextPage || index < itemData.length;
  };

  const calculateDirection = () => {
    console.log('calculate direction');
    if (!selectComponentRef.current || !popUpContainerRef.current) return;

    const rect = selectComponentRef.current.getBoundingClientRect();
    const spaceAbove = rect.top;
    const spaceBelow =
      window.innerHeight -
      rect.bottom -
      selectComponentRef.current.clientHeight;

    const dropdownHeight =
      (popUpContainerRef.current.parentElement?.clientHeight ?? 0) +
      DEFAULT_MUI_BORDER;

    const selectComponentHeight =
      selectComponentRef.current.clientHeight + DEFAULT_MUI_BORDER;

    if (popUpContainerRef.current.clientHeight) {
      setCalculatedDropdownHeight(
        dropdownHeight + selectComponentHeight + MUI_CANVAS_ADJUSTMENT
      );
    }

    if (
      props.anchorBottonPosition ||
      spaceBelow >= spaceAbove ||
      spaceBelow >= dropdownHeight
    ) {
      setMenuPosition(selectComponentHeight);
    } else {
      setMenuPosition((dropdownHeight + CANVAS_UPPER_ADJUSTMENT) * -1);
    }
  };

  const searchItems = async (keyword: string) => {
    if (onSearch) {
      if (keyword.length > 0) {
        setIsSearching(true);
        setSearchTerm(keyword);
      } else {
        //Reset search state
        setIsSearching(false);
        setSearchTerm('');
        setSearchResults([]);
      }
    }
  };

  const loadSearchResults = async (keyword: string) => {
    if (!onSearch) return;

    setSearchResults([]);

    try {
      const newItems = await onSearch(keyword);

      if (newItems.length > 0) setSearchResults([...newItems]);
      else
        setSearchResults([
          {key: '1', value: 'noResults', label: t('common.noResult')},
        ]);
    } catch (e) {
      console.error(e);
    }
  };

  let isPaging = false;
  const loadMoreItems = async (startIndex: number, _stopIndex: number) => {
    if (isLoading) return;

    if (onPage && !isPaging && !isSearching) {
      isPaging = true;
      try {
        const newItems = await onPage(startIndex, startIndex + itemsPerPage);
        setItemData([...itemData, ...newItems]);
      } finally {
        isPaging = false;
      }
    }
  };

  const renderSelectItem = (props: ListChildComponentProps) => {
    const {index, style} = props;

    let component = <></>;
    const item = getItemData()[index];

    if (!isItemLoaded(index) || !item) {
      // Render "Loading..."
      component = (
        <div style={style}>
          <MenuItem disabled value="">
            <span>
              <CircularProgress size={18} color="inherit" thickness={5} />
            </span>
            &nbsp;
            <span>{t('searchableDropdown.loading')}</span>
          </MenuItem>
        </div>
      );
    } else {
      // Render item
      if (item?.value === 'noResults') {
        component = (
          <div
            className="select-search-no-results"
            style={style}
            onClick={() => onClickDelegate(item)}
          >
            {item.label as string}
          </div>
        );
      } else {
        component = (
          <div style={style} className={customProjectClassName}>
            <MenuItem
              key={item.key}
              value={item.value}
              className={item === selectedItem ? 'Mui-selected' : ''}
              tabIndex={0}
              onClick={() => onClickDelegate(item)}
              onKeyUp={(e) => onKeyUpDelegate(e, item)}
            >
              <div className="dropdown-item-container">
                {(item as Option).displayElement ? (
                  (item as Option).displayElement
                ) : (
                  <ListItemText primary={(item as Option).label as string} />
                )}
              </div>
              {item === selectedItem ? (
                <AurIcon
                  icon={IconNames.CheckMark}
                  customClassName="check-icon"
                  color={IconColors.Custom}
                />
              ) : (
                <></>
              )}
            </MenuItem>
          </div>
        );
      }
    }

    return component;
  };

  const getItemData = () => {
    if (isSearching) {
      if (searchResults.length > 0) return searchResults;
      return [];
    } else return itemData;
  };

  const getItemCount = () => {
    if (hasNextPage && !isSearching) {
      return itemData.length + 1;
    }
    if (isSearching) {
      if (searchResults.length > 0) return searchResults.length;
      return 1;
    } else return itemData.length;
  };

  const getAvgItemHeight = (): number => {
    if (itemData.length < 1) return ITEM_HEIGHT;

    if (isVariableHeight) {
      const heightSum = itemData.reduce(
        (n, item) => (n += item.height || 0),
        0
      );
      return heightSum / itemData.length;
    }

    return ITEM_HEIGHT;
  };

  const getVisibleItemHeight = () => {
    const itemCount = getItemCount();
    let height = 0;
    const itemHeight = isVariableHeight ? getAvgItemHeight() : ITEM_HEIGHT;

    if (itemsVisible > itemCount) {
      height += itemCount * itemHeight;
    } else {
      height += itemsVisible * itemHeight;

      if (searchable) {
        height -= SEARCH_BOX_HEIGHT;
      }
    }
    return height;
  };

  return (
    <FormControl
      variant="filled"
      error={!!fieldState.error}
      disabled={disabled}
      style={style}
      className={`${borderClass}`}
    >
      <div className={' aur-select-client'}>
        <InputLabel
          id={genId}
          className={`select-label ${fieldState.error?.message && 'label-error'}`}
        >
          {label}
        </InputLabel>
        <Select
          ref={selectComponentRef}
          labelId={genId}
          label={field.label}
          value={selectedItem.value}
          disabled={disabled ? disabled : false}
          open={isDropdownOpen}
          onOpen={() => setIsDropdownOpen(true)}
          onClose={() => setIsDropdownOpen(false)}
          data-testid={dataTestId}
          sx={{width: '100%'}}
          MenuProps={{
            disableScrollLock: true,
            disablePortal: true,
            anchorReference: 'none',
            sx: {
              position: 'absolute',
              height: `${calculatedDropdownHeight}px`,
              ...conditionalPositioningProp,
            },
            TransitionProps: {
              onEnter: calculateDirection,
            },
            PaperProps: {
              sx: {
                '& .MuiMenu-list': {
                  overflowY: 'auto',
                },
                border: '3px solid #546A8C !Important',
                borderRadius: '8px',
                marginTop: '6px',
                boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
              },
            },
          }}
          renderValue={(_value) => {
            return selectedItem.label ?? 'Select any Option';
          }}
          onFocus={focusHandler}
          onMouseDown={mouseKeyDownHandler}
        >
          {searchable ? (
            <ListSubheader className="select-search-container">
              <Box
                className="select-search-box"
                onKeyDown={(e) => {
                  if (e.key !== 'Escape') e.stopPropagation();
                }}
              >
                <AurSearchBox
                  debounceTimeout={800}
                  minCharForSearch={4}
                  onSearch={searchItems}
                />
              </Box>
              <Divider className={`${styles.aurMultiSelectPanelDivider}`} />
            </ListSubheader>
          ) : (
            <></>
          )}

          <Box
            ref={popUpContainerRef}
            onKeyDown={(e) => {
              if (e.key !== 'Escape') e.stopPropagation();
            }}
            tabIndex={-1}
          >
            <InfiniteLoader
              isItemLoaded={isItemLoaded}
              itemCount={getItemCount()}
              loadMoreItems={loadMoreItems}
              minimumBatchSize={itemsPerPage}
            >
              {({onItemsRendered, ref}) =>
                isVariableHeight ? (
                  <VariableSizeList
                    onItemsRendered={onItemsRendered}
                    ref={ref}
                    innerRef={(elementRefOrNull) => {
                      if (elementRefOrNull) {
                        elementRefOrNull.tabIndex = -1;
                      }
                    }}
                    outerRef={(elementRefOrNull) => {
                      if (elementRefOrNull) {
                        elementRefOrNull.tabIndex = -1;
                      }
                    }}
                    height={getVisibleItemHeight()}
                    itemCount={getItemCount()}
                    itemSize={(index) =>
                      optionList[index].height
                        ? (optionList[index].height as number)
                        : ITEM_HEIGHT
                    }
                    width={'100%'}
                    className={`${SCROLL_BAR_STYLE_CLASS} ${styles.aurItemContainerScrollbar}`}
                  >
                    {renderSelectItem}
                  </VariableSizeList>
                ) : (
                  <FixedSizeList
                    onItemsRendered={onItemsRendered}
                    ref={ref}
                    innerRef={(elementRefOrNull) => {
                      if (elementRefOrNull) {
                        elementRefOrNull.tabIndex = -1;
                      }
                    }}
                    outerRef={(elementRefOrNull) => {
                      if (elementRefOrNull) {
                        elementRefOrNull.tabIndex = -1;
                      }
                    }}
                    height={getVisibleItemHeight()}
                    itemCount={getItemCount()}
                    itemSize={ITEM_HEIGHT}
                    width={'100%'}
                    className={`${SCROLL_BAR_STYLE_CLASS} ${styles.aurItemContainerScrollbar}`}
                  >
                    {renderSelectItem}
                  </FixedSizeList>
                )
              }
            </InfiniteLoader>
          </Box>
        </Select>
      </div>
      <FormHelperText>
        {!fieldState.error?.message && helperText}
      </FormHelperText>
      <FormHelperText className={fieldState.error?.message && 'label-error'}>
        {fieldState.error?.message}
      </FormHelperText>
    </FormControl>
  );
};

export default AurServerSingleSelect;
